import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { ChevronDown, User, Settings, LogOut } from 'lucide-react';
import { ROUTES, API } from '../../../../core/config';

interface UserMenuProps {
  userName?: string;
  initials?: string;
  onLogout?: () => void;
  isAuthenticated?: boolean;
}

const dropdownVariants: Variants = {
  hidden: { opacity: 0, scale: 0.95, y: -8 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.15, ease: 'easeOut' },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: -8,
    transition: { duration: 0.1, ease: 'easeIn' },
  },
};

const UserMenu: React.FC<UserMenuProps> = ({
  userName = 'Utilisateur',
  initials = 'U',
  onLogout,
  isAuthenticated = false,
}) => {
  const navigate = useNavigate();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleLogout = async () => {
    setIsDropdownOpen(false);
    await onLogout?.();
  };

  // Navigation vers MyAccount
  const goToMyAccount = (path: string) => {
    const baseUrl = API.MYACCOUNT_APP_URL;
    const targetUrl = `${baseUrl}${path}`;
    window.location.href = targetUrl;
  };

  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isDropdownOpen]);

  // ❌ non auth
  if (!isAuthenticated) {
    return (
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => navigate(ROUTES.AUTH_ROUTES.LOGIN)}
        className="bg-accent text-white px-6 py-2 rounded-full text-sm font-bold shadow-md hover:shadow-lg transition-all"
      >
        Connexion
      </motion.button>
    );
  }

  // ✅ auth
  return (
    <div ref={containerRef} className="relative">
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.97 }}
        onClick={() => setIsDropdownOpen((prev) => !prev)}
        className="flex items-center space-x-3 px-3 py-1.5 rounded-full hover:bg-gray-50 transition-colors focus:outline-none"
      >
        <div className="relative">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-accent to-secondary text-white flex items-center justify-center font-bold text-sm shadow-sm">
            {initials}
          </div>

          <motion.div
            className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </div>

        <span className="text-sm font-bold text-gray-700 hidden sm:block">
          {userName}
        </span>

        <motion.div
          animate={{ rotate: isDropdownOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="w-4 h-4 text-gray-400 hidden sm:block" />
        </motion.div>
      </motion.button>

      <AnimatePresence>
        {isDropdownOpen && (
          <motion.div
            variants={dropdownVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-2xl border border-gray-100/80 py-2 z-50 overflow-hidden"
          >
            <div className="px-4 py-2 border-b border-gray-100">
              <p className="text-sm font-semibold text-gray-800">{userName}</p>
              <p className="text-xs text-gray-500">Authentifié</p>
            </div>

            <button
              onClick={() => {
                setIsDropdownOpen(false);
                goToMyAccount('/profile');
              }}
              className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <User className="w-4 h-4 mr-3 text-accent" />
              Profil
            </button>

            <button
              onClick={() => {
                setIsDropdownOpen(false);
                goToMyAccount('/security');
              }}
              className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Settings className="w-4 h-4 mr-3 text-accent" />
              Paramètres
            </button>

            <hr className="my-1 border-gray-100" />

            <button
              onClick={handleLogout}
              className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut className="w-4 h-4 mr-3" />
              Déconnexion
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserMenu;